import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import * as wizeMobileApi from '@wize-mobile-api';
import { EMPTY, firstValueFrom, forkJoin, from, lastValueFrom, Observable, of, throwError, timer } from 'rxjs';
import { catchError, debounce, switchMap, take, tap } from 'rxjs/operators';
import { PaymentMapper } from '~/modules/payments/payment.mapper';
import {
  loadPaymentCategories,
  loadPaymentRecentPayments,
  loadSwiftCodeFromIban,
  loadSwiftCodeFromIbanFailure,
  loadSwiftCodeFromIbanSuccess,
  selectOrdersPaymentRequests,
  selectPaymentCategories,
  selectSwiftCodeFromIbanMapping,
  selectSwiftCodes,
  updatePaymentOrdersRequests
} from '~/modules/payments/slice';
import { selectPortfolios } from '~/modules/portfolio/slice';
import { selectUserRole } from '@qvs/core/user';
import { Payment, PaymentCategory, SwiftCode, Transfer, UserRoleEnum } from '@qvs/core';
import { OrdersPaymentRequest } from '~/lazy/payments/pages/new-payment/models/ordersPaymentRequest';
import { AppService } from '~/modules/app/app.service';
import { selectAllCountries } from '~/modules/countries/slice';
import { loadCountries } from '~/modules/countries/slice/country.actions';
import { URLOpenListenerEvent } from '@capacitor/app/dist/esm/definitions';
import { AppSwitchError } from '~/modules/app/error/AppSwitchError';
import { environment } from '#environment';
import { loadExchangeRates } from '~/modules/exchangeRates/slice';
import { loadCurrenciesOnlyIfNotLoadedYet } from '~/modules/currencies/slice';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly idMustNotBeEmpty = 'paymentId must not be empty';

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly paymentsApi: wizeMobileApi.TransferOrderService,
    private readonly swiftApi: wizeMobileApi.BasicDataMobileService,
    private readonly analyticalCodeTypesApi: wizeMobileApi.AnalyticalCodeTypesService,
    private readonly appService: AppService
  ) {}

  init(): Promise<void> {
    this.store.select(selectUserRole).subscribe((userRole) => {
      if (userRole) {
        this.afterUserHasRole();
      }
    });
    return Promise.resolve();
  }

  loadRecentPayments(): Observable<Payment[]> {
    return this.store.select(selectUserRole).pipe(
      switchMap((userRole) =>
        userRole === UserRoleEnum.CLIENT
          ? this.paymentsApi.getLastPayments(100).pipe(
              switchMap((payments) => of(PaymentMapper.fromWizePayments(payments))),
              catchError((e) => throwError(() => e))
            )
          : of([])
      )
    );
  }

  public loadPayment(transactionId: number): Observable<Payment> {
    return this.paymentsApi.getPayment(transactionId).pipe(
      switchMap((payment) => of(PaymentMapper.fromWizePayment(payment))),
      catchError((e) => throwError(() => e))
    );
  }

  loadRecentTransfers(): Observable<Transfer[]> {
    return this.store.select(selectUserRole).pipe(
      switchMap((userRole) =>
        userRole === UserRoleEnum.CLIENT
          ? forkJoin({
              transfers: this.paymentsApi.getLastInternalTransfer(100),
              portfolios: this.store.select(selectPortfolios).pipe(take(1))
            }).pipe(
              switchMap(({ transfers, portfolios }) => of(PaymentMapper.fromWizeTransfers(transfers, portfolios))),
              catchError((e) => throwError(() => e))
            )
          : of([])
      )
    );
  }

  loadCategories(): Observable<PaymentCategory[]> {
    return this.store.select(selectUserRole).pipe(
      switchMap((userRole) =>
        userRole === UserRoleEnum.CLIENT
          ? forkJoin({
              categories: lastValueFrom(this.analyticalCodeTypesApi.listAnalyticalCodeTypes()).catch()
            }).pipe(
              switchMap(({ categories }) => of(PaymentMapper.fromWizeCategories(categories))),
              catchError((e) => throwError(() => e))
            )
          : of([])
      )
    );
  }

  createOrderPayment(ordersPaymentRequest: OrdersPaymentRequest): Observable<wizeMobileApi.PaymentMobileCreationResponse> {
    return this.store.select(selectPaymentCategories).pipe(
      take(1),
      switchMap((categories) =>
        this.paymentsApi.createPaymentForm(
          ordersPaymentRequest.uuid,
          false,
          PaymentMapper.toWizePaymentRequest(ordersPaymentRequest, categories)
        )
      ),
      switchMap((paymentCreationResponse) => {
        if (!paymentCreationResponse) {
          return throwError(() => paymentCreationResponse);
        }
        return of(paymentCreationResponse);
      }),
      catchError((e) => throwError(() => e))
    );
  }

  validateOrderPayment(ordersPaymentRequest: OrdersPaymentRequest): Observable<wizeMobileApi.Violation[]> {
    return this.store.select(selectPaymentCategories).pipe(
      take(1),
      switchMap((categories) =>
        this.paymentsApi.createPaymentForm(
          ordersPaymentRequest.uuid,
          true,
          PaymentMapper.toWizePaymentRequest(ordersPaymentRequest, categories),
          'events'
        )
      ),
      switchMap(() => of(null)),
      catchError((paymentCreationResponse) => {
        if (paymentCreationResponse) {
          return of(Object.values(paymentCreationResponse.error.error.violations));
        }
        return of(null);
      })
    );
  }

  authorizeOrderPayment(
    ordersPaymentRequest: OrdersPaymentRequest
  ): Observable<wizeMobileApi.ApiOrderInputFormAuthorizationCreationResponse> {
    const response: wizeMobileApi.PaymentMobileCreationResponse = ordersPaymentRequest.paymentCreationResponse as any;
    if (!response?.paymentId) {
      return throwError(() => new Error(this.idMustNotBeEmpty));
    }
    return forkJoin({
      authorizationResponse: lastValueFrom(this.paymentsApi.createPaymentAuthorization(response.paymentId)).catch()
    }).pipe(
      switchMap(({ authorizationResponse }) => {
        if (!authorizationResponse) {
          return throwError(() => authorizationResponse);
        }
        return of(authorizationResponse);
      }),
      catchError((e) => throwError(() => e))
    );
  }

  confirmAuthorizedOrdersPayment(
    ordersPaymentRequest: OrdersPaymentRequest,
    isAccessAppAvailable: boolean
  ): Observable<wizeMobileApi.PaymentAuthorizationResponse> {
    const response: wizeMobileApi.PaymentMobileCreationResponse = ordersPaymentRequest.paymentCreationResponse as any;
    if (!response?.paymentId) {
      return throwError(() => new Error(this.idMustNotBeEmpty));
    }
    return this.authorizePayment(
      response.paymentId,
      ordersPaymentRequest.paymentAuthorizationResponse.authorizationCode,
      isAccessAppAvailable ? 'appSwitch' : 'push'
    );
  }

  createOrderInternalTransfer(
    ordersPaymentRequest: OrdersPaymentRequest
  ): Observable<wizeMobileApi.InternalTransferMobileCreationResponse> {
    return this.store.select(selectPaymentCategories).pipe(
      take(1),
      switchMap((categories) =>
        this.paymentsApi.createInternalTransferForm(
          ordersPaymentRequest.uuid,
          false,
          PaymentMapper.toWizeInternalTransferRequest(ordersPaymentRequest, categories)
        )
      ),
      switchMap((paymentCreationResponse) => {
        if (!paymentCreationResponse) {
          return throwError(() => paymentCreationResponse);
        }
        return of(paymentCreationResponse);
      }),
      catchError((e) => throwError(() => e))
    );
  }

  validateOrderInternalTransfer(ordersPaymentRequest: OrdersPaymentRequest): Observable<wizeMobileApi.Violation[]> {
    return this.store.select(selectPaymentCategories).pipe(
      take(1),
      switchMap((categories) =>
        this.paymentsApi.createInternalTransferForm(
          ordersPaymentRequest.uuid,
          true,
          PaymentMapper.toWizeInternalTransferRequest(ordersPaymentRequest, categories),
          'events'
        )
      ),
      switchMap(() => of(null)),
      catchError((paymentCreationResponse) => {
        if (paymentCreationResponse) {
          return of(Object.values(paymentCreationResponse.error.error.violations));
        }
        return of(null);
      })
    );
  }

  authorizeOrderInternalTransfer(
    ordersPaymentRequest: OrdersPaymentRequest
  ): Observable<wizeMobileApi.ApiOrderInputFormAuthorizationCreationResponse> {
    const response: wizeMobileApi.InternalTransferMobileCreationResponse = ordersPaymentRequest.paymentCreationResponse as any;
    if (!response?.transferId) {
      return throwError(() => new Error(this.idMustNotBeEmpty));
    }
    return forkJoin({
      authorizationResponse: lastValueFrom(this.paymentsApi.createInternalTransferAuthorization(response.transferId)).catch()
    }).pipe(
      switchMap(({ authorizationResponse }) => {
        if (!authorizationResponse) {
          return throwError(() => authorizationResponse);
        }
        return of(authorizationResponse);
      }),
      catchError((e) => throwError(() => e))
    );
  }

  confirmAuthorizedOrdersInternalTransfer(
    ordersPaymentRequest: OrdersPaymentRequest,
    isAccessAppAvailable: boolean
  ): Observable<wizeMobileApi.PaymentAuthorizationResponse> {
    const response: wizeMobileApi.InternalTransferMobileCreationResponse = ordersPaymentRequest.paymentCreationResponse as any;
    if (!response?.transferId) {
      return throwError(() => new Error(this.idMustNotBeEmpty));
    }
    return this.authorizeInternalTransfer(
      response.transferId,
      ordersPaymentRequest.paymentAuthorizationResponse.authorizationCode,
      isAccessAppAvailable ? 'appSwitch' : 'push'
    );
  }

  loadSwiftCodes(country: string): Observable<SwiftCode[]> {
    return this.store.select(selectUserRole).pipe(
      switchMap((userRole) =>
        userRole === UserRoleEnum.CLIENT
          ? forkJoin({
              existingSwiftCodes: this.store.select(selectSwiftCodes).pipe(take(1)),
              countries: this.store.select(selectAllCountries).pipe(take(1)),
              swiftCodes: lastValueFrom(this.swiftApi.listSwiftBicCodes(country)).catch()
            }).pipe(
              switchMap(({ existingSwiftCodes, swiftCodes, countries }) =>
                of(PaymentMapper.fromWizeSwiftCodes(swiftCodes, existingSwiftCodes, countries))
              ),
              catchError((e) => throwError(() => e))
            )
          : of([])
      )
    );
  }

  loadSwiftCodeFromIban(iban: string): Observable<SwiftCode> {
    return forkJoin({
      countries: this.store.select(selectAllCountries).pipe(take(1)),
      swiftCode: lastValueFrom(this.swiftApi.getSwiftBicCodeFromIban(iban)).catch()
    }).pipe(
      switchMap(({ swiftCode, countries }) => {
        if (!swiftCode) {
          return throwError(() => swiftCode);
        }
        return of(PaymentMapper.fromWizeSwiftCodeToSwiftCode(swiftCode, countries));
      }),
      catchError((e) => throwError(() => e))
    );
  }

  loadPaymentOrdersRequest(ordersPaymentRequest: OrdersPaymentRequest): Observable<OrdersPaymentRequest> {
    const response: wizeMobileApi.PaymentMobileCreationResponse = ordersPaymentRequest.paymentCreationResponse as any;
    const paymentId = ordersPaymentRequest?.id || response?.paymentId || null;
    if (!paymentId) {
      return EMPTY;
    }
    return forkJoin({
      countries: this.store.select(selectAllCountries).pipe(take(1)),
      payment: lastValueFrom(this.paymentsApi.getPayment(paymentId))
    }).pipe(
      switchMap(({ payment, countries }) => {
        if (!payment) {
          return throwError(() => payment);
        }
        return of(PaymentMapper.fromWizePaymentMobileToOrdersPaymentRequest(payment, countries, ordersPaymentRequest));
      }),
      catchError((e) => throwError(() => e))
    );
  }

  loadPaymentInternalTransferRequest(ordersPaymentRequest: OrdersPaymentRequest): Observable<OrdersPaymentRequest> {
    const response: wizeMobileApi.InternalTransferMobileCreationResponse = ordersPaymentRequest.paymentCreationResponse as any;
    const transferId = ordersPaymentRequest?.id || response?.transferId || null;
    if (!transferId) {
      return EMPTY;
    }
    return this.paymentsApi.getInternalTransfers(transferId).pipe(
      switchMap((transfer) => {
        if (!transfer) {
          return throwError(() => transfer);
        }
        return of(PaymentMapper.fromWizeInternalTransferMobileToOrdersPaymentRequest(transfer, ordersPaymentRequest));
      }),
      catchError((e) => throwError(() => e))
    );
  }

  async getSwiftCodeFromIban(iban: string): Promise<SwiftCode> {
    let swiftCodeMapping = await firstValueFrom(this.store.select(selectSwiftCodeFromIbanMapping));
    if (swiftCodeMapping[iban]) {
      return Promise.resolve(swiftCodeMapping[iban]);
    }
    this.store.dispatch(loadSwiftCodeFromIban({ iban }));
    await firstValueFrom(this.actions$.pipe(ofType(loadSwiftCodeFromIbanSuccess, loadSwiftCodeFromIbanFailure)));
    swiftCodeMapping = await firstValueFrom(this.store.select(selectSwiftCodeFromIbanMapping));
    return Promise.resolve(swiftCodeMapping[iban] || ({} as any));
  }

  private authorizePayment(
    paymentId: number,
    authorizationCode: number,
    method: 'push' | 'appSwitch'
  ): Observable<wizeMobileApi.PaymentAuthorizationResponse> {
    return this.paymentsApi
      .authorizePayment(paymentId, authorizationCode, undefined, method === 'appSwitch' ? 'qrcode' : undefined)
      .pipe(
        switchMap((confirmResponse) => this.handleAuthorizationResponse(confirmResponse)),
        catchError((error) => throwError(() => error))
      );
  }

  private authorizeInternalTransfer(
    paymentId: number,
    authorizationCode: number,
    method: 'push' | 'appSwitch'
  ): Observable<wizeMobileApi.PaymentAuthorizationResponse> {
    return this.paymentsApi
      .authorizeInternalTransfer(paymentId, authorizationCode, undefined, method === 'appSwitch' ? 'qrcode' : undefined)
      .pipe(
        switchMap((confirmResponse) => this.handleAuthorizationResponse(confirmResponse)),
        catchError((error) => throwError(() => error))
      );
  }

  private handleAuthorizationResponse(confirmResponse: wizeMobileApi.PaymentAuthorizationResponse) {
    if (!confirmResponse) {
      return throwError(() => confirmResponse);
    }
    if (confirmResponse.appLinkUri) {
      let appLinkUri = confirmResponse.appLinkUri;
      if (!appLinkUri.includes(environment.accessApp.uri)) {
        appLinkUri = appLinkUri.replace(/^[^?]+/, environment.accessApp.uri);
      }
      const paymentConfirmation = environment.accessApp.deepLinks.paymentConfirmation;
      //eslint-disable-next-line max-len
      const url = `${appLinkUri}&x-success=${paymentConfirmation.success}&x-cancel=${paymentConfirmation.cancel}&x-error=${paymentConfirmation.error}`;
      return from(this.appService.switchToAccessApp(url)).pipe(
        switchMap((event: URLOpenListenerEvent) =>
          event.url === paymentConfirmation.success ? of(confirmResponse) : throwError(() => new AppSwitchError())
        ),
        catchError(() => throwError(() => new AppSwitchError()))
      );
    }
    return of(confirmResponse);
  }

  private afterUserHasRole() {
    this.store
      .select(selectPortfolios)
      .pipe(debounce(() => timer(1000)))
      .subscribe((portfolios) => {
        if (portfolios.length > 0) {
          this.afterPortfoliosChange();
        }
      });
    this.store
      .select(selectUserRole)
      .pipe(debounce(() => timer(1000)))
      .subscribe((userRole) => {
        if (userRole) {
          this.afterUserRoleChange();
        }
      });
    this.store
      .select(selectOrdersPaymentRequests)
      .pipe(
        tap((requests) => {
          if (requests.size > 0) {
            this.store.dispatch(updatePaymentOrdersRequests({ requests }));
          }
        })
      )
      .subscribe();
  }

  private afterPortfoliosChange() {
    this.store.dispatch(loadCurrenciesOnlyIfNotLoadedYet());
    this.store.dispatch(loadPaymentRecentPayments({ loadOnlyIfEmpty: true }));
  }

  private afterUserRoleChange() {
    this.store.dispatch(loadCountries());
    this.store.dispatch(loadPaymentCategories());
    this.store.dispatch(loadExchangeRates());
  }
}
